/**
 * THE RUNNING MID-MONTH EARNINGS FIGURE.
 *
 * Ruled by the owner on 2026-08-26, decision-log row 138 item 1 — option (b), "why not, if thats
 * not a problem": an author sees a running figure while the month is still open, "worked out fresh
 * each time it is asked for". It creates NO STORED BALANCE, adds NO TABLE and NO COLUMN, and it is
 * an ADDITION TO `getPaymentsView` rather than a second operation. So this component takes
 * `PaymentsView` and reads `currentMonth` off it; there is nothing else to call.
 *
 * FOUR THINGS THIS COMPONENT EXISTS TO GET RIGHT, each of which is a way to be quietly wrong:
 *
 *  1. THE ARITHMETIC. `projectedTotalMinor` is `availableMinor + articlesMinor` and NOTHING ELSE.
 *     This month's tips are ALREADY INSIDE `availableMinor` — a tip writes its ledger movement when
 *     it lands, while article earnings reach the ledger only at the payout run — so `tipsMinor` is
 *     a breakdown of money already counted, never a third addend. Adding it again overstates an
 *     author's month by their own tips. This component NEVER ADDS ANYTHING: it prints the server's
 *     `projectedTotalMinor` and shows the two parts underneath as an explanation.
 *
 *  2. IT IS NOT FINAL AND NEVER CLAIMS TO BE. `isFinal` is a constant `false` on this object. The
 *     word "estimate" is on screen, not implied.
 *
 *  3. IT CAN GO DOWN, AND A DECREASE IS NOT AN ERROR. Hiding a comment removes a countable comment;
 *     a projection recompute can move counted views. There is no high-water mark. The screen says
 *     so, because an author who watches a number fall and is told nothing files a support ticket.
 *
 *  4. ITS LAST DAY MAY BE INCOMPLETE. `countedThrough` names the last FULLY COUNTED UTC day, and it
 *     is printed rather than implied — the rollup runs on a cadence, so today is partial or absent.
 *
 * ★ THE PROTOTYPE'S LOOK, IN PLAIN WORDS (design 15 - the owner's live look at real cabinets,
 * 2026-10-01). The figure sits in a card under the small capitals "YOUR BALANCE WITH THIS MONTH'S EARNINGS"
 * (`SectionLabel`, the block's `<h2>`): the estimate large, "Counted up to Aug 26, 2026" beside it,
 * and its two parts as the rows of a breakdown, the way the prototype's "This month in detail"
 * draws one. The owner's rule - an author is a user - took out every internal word the card used to
 * say: no "ledger" (the author's word is "balance"), no "UTC", no "worked out fresh each time you
 * look", no "recomputed", no "priced by a payout run". The four things above still hold, word for
 * word in what the card claims: it adds nothing, says "an estimate", says it can go down, and
 * prints the day it is counted to. Since design 15 only the Overview draws this card; Payments
 * draws the same answer as its own "This month in detail" (../payments/PaymentsRoute.tsx).
 */
import { Card, Stack, Text } from '@mantine/core';
import { useId } from 'react';
import { Money, SectionLabel } from '../../features/cabinet/components';
import { formatDay, formatPeriod } from '../../features/cabinet/format';
import type { PaymentsView } from '../../features/cabinet/api';
import { Trans, useTranslation } from '../../i18n';
import classes from './overview.module.css';

export function MidMonthEarnings({ payments }: { payments: PaymentsView }) {
  const { t } = useTranslation('dashboard');
  const headingId = useId();
  const month = payments.currentMonth;

  if (month === undefined) {
    // A legitimate state, not a failure: `currentMonth` is optional, and it "exists only while
    // there is no run to answer with". Its absence means the month has closed and been priced.
    return (
      <section aria-labelledby={headingId}>
        <SectionLabel id={headingId}>{t('month.heading')}</SectionLabel>
        <Card padding="lg">
          <Text size="sm">{t('month.noEstimate')}</Text>
        </Card>
      </section>
    );
  }

  return (
    <section aria-labelledby={headingId}>
      <SectionLabel id={headingId}>{t('month.heading')}</SectionLabel>
      <Card padding="lg">
        <Stack gap="md">
          <div className={classes.monthHead}>
            <div>
              <div className={classes.monthLabel}>
                {t('month.soFar', { month: formatPeriod(month.period) })}
              </div>
              {/* The SERVER's number. Nothing on this screen adds two amounts together. */}
              <Money
                minor={month.projectedTotalMinor}
                currency={payments.currency}
                emphasiseNegative
                fz={27}
                fw={800}
                lh={1.15}
                style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
              />
            </div>
            <div className={classes.countedTo}>
              {t('month.countedTo', { day: formatDay(month.countedThrough) })}
            </div>
          </div>

          <Stack gap={8}>
            <Text size="xs" c="dimmed">
              {t('month.madeOf')}
            </Text>
            <ul className={classes.lines}>
              <li className={classes.line}>
                <div className={classes.lineText}>
                  <div className={classes.lineLabel}>{t('month.available.label')}</div>
                  <div className={classes.lineNote}>
                    {month.tipsMinor === 0
                      ? t('month.available.note')
                      : t('month.available.noteWithTips')}
                  </div>
                </div>
                <Money
                  minor={payments.availableMinor}
                  currency={payments.currency}
                  emphasiseNegative
                  size="sm"
                  fw={800}
                  className={classes.lineAmount}
                />
              </li>
              <li className={classes.line}>
                <div className={classes.lineText}>
                  <div className={classes.lineLabel}>{t('month.articles.label')}</div>
                  <div className={classes.lineNote}>{t('month.articles.note')}</div>
                </div>
                <Money
                  minor={month.articlesMinor}
                  currency={payments.currency}
                  size="sm"
                  fw={800}
                  className={classes.lineAmount}
                />
              </li>
            </ul>
          </Stack>

          {month.tipsMinor === 0 ? null : (
            <Text size="xs" c="dimmed">
              <Trans
                t={t}
                i18nKey="month.tipsOfBalance"
                components={{
                  money: (
                    <Money
                      minor={month.tipsMinor}
                      currency={payments.currency}
                      span
                      emphasiseNegative
                    />
                  ),
                }}
              />
            </Text>
          )}

          <Stack gap={4} className={classes.monthFoot}>
            <Text inherit>{t('month.notFinal')}</Text>
            {month.meetsThresholdProjected === undefined ? null : (
              <Text inherit>
                {month.meetsThresholdProjected
                  ? t('month.wouldReachMinimum')
                  : t('month.wouldNotReachMinimum')}
              </Text>
            )}
          </Stack>
        </Stack>
      </Card>
    </section>
  );
}
